import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, Edit, Trash2, CalendarOff, 
  CheckCircle, Loader2, Calendar,
  Download, RefreshCw, Eye, Clock
} from "lucide-react";
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';
import { ResponsiveMetricsGrid } from '@/components/ui/responsive-charts';
import { ResponsiveTable, ColumnDef } from '@/components/ui/responsive-table';
import { useTableColumns } from '@/hooks/useTableColumns';
import { ResponsiveForm, TouchInput } from '@/components/ui/responsive-form';
import { useResponsive } from '@/hooks/useResponsive';
import { DiaNaoFuncionamentoService } from '@/services/diaNaoFuncionamentoService';
import { DiaNaoFuncionamento, CreateDiaNaoFuncionamentoRequest, DiaNaoFuncionamentoEstatisticas, DiaNaoFuncionamentoStatistics, DiaNaoFuncionamentoEstatisticasDetalhadas, TipoDiaNaoFuncionamento } from '@/types/diaNaoFuncionamento';
import { toast } from 'sonner';
import { useDateFormatter } from '@/hooks/useDateFormatter';

const AdminNonWorkingDays = () => {
  const { formatCompact, formatForInput, isValidDate } = useDateFormatter();
  const { isMobile } = useResponsive();
  const [nonWorkingDays, setNonWorkingDays] = useState<DiaNaoFuncionamento[]>([]);
  const [statistics, setStatistics] = useState<DiaNaoFuncionamentoStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDay, setEditingDay] = useState<DiaNaoFuncionamento | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewYear, setPreviewYear] = useState(new Date().getFullYear());
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHolidays, setPreviewHolidays] = useState<Array<{ data: string; nome: string; tipo: string }>>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dayToDelete, setDayToDelete] = useState<DiaNaoFuncionamento | null>(null);
  const [formData, setFormData] = useState({
    data: "",
    motivo: "",
    descricao: "",
    ativo: true,
    recorrente: false,
    tipo: TipoDiaNaoFuncionamento.FERIADO
  });

  // Métricas essenciais dos dias não funcionamento (removido "Recorrentes")
  const metrics = statistics ? [
    {
      title: "Total de Dias",
      value: (statistics.totalDias || 0).toString(),
      change: `${statistics.diasAtivos || 0} ativos`,
      bg: "bg-gradient-to-r from-blue-500 to-cyan-500",
      icon: CalendarOff
    },
    {
      title: "Dias Ativos",
      value: (statistics.diasAtivos || 0).toString(),
      change: `${Math.round(((statistics.diasAtivos || 0) / Math.max(statistics.totalDias || 1, 1)) * 100)}% do total`,
      bg: "bg-gradient-to-r from-green-500 to-emerald-500",
      icon: CheckCircle
    },
    {
      title: "Feriados",
      value: (statistics.porTipo?.FERIADO || 0).toString(),
      change: `${statistics.porTipo?.FERIADO || 0} registrados`,
      bg: "bg-gradient-to-r from-purple-500 to-pink-500",
      icon: Calendar
    },
    {
      title: "Automáticos",
      value: (statistics.diasAutomaticos || 0).toString(),
      change: "feriados nacionais",
      bg: "bg-gradient-to-r from-indigo-500 to-purple-500",
      icon: RefreshCw
    }
  ] : [
    // Loading state cards
    {
      title: "Total de Dias",
      value: "...",
      change: "carregando...",
      bg: "bg-gradient-to-r from-blue-500 to-cyan-500",
      icon: CalendarOff
    },
    {
      title: "Dias Ativos", 
      value: "...",
      change: "carregando...",
      bg: "bg-gradient-to-r from-green-500 to-emerald-500",
      icon: CheckCircle
    },
    {
      title: "Feriados",
      value: "...",
      change: "carregando...",
      bg: "bg-gradient-to-r from-purple-500 to-pink-500",
      icon: Calendar
    },
    {
      title: "Automáticos",
      value: "...",
      change: "carregando...",
      bg: "bg-gradient-to-r from-indigo-500 to-purple-500",
      icon: RefreshCw
    }
  ];

  // ✅ NOVA FUNCIONALIDADE: Carregar todos os dias (incluindo automáticos) na tela inicial
  const loadManualDays = async () => {
    try {
      setLoading(true);
      const [daysResponse, statsResponse] = await Promise.all([
        DiaNaoFuncionamentoService.getDiasNaoFuncionamento(),
        DiaNaoFuncionamentoService.getEstatisticas()
      ]);

      // Exibir todos os dias (manuais e automáticos)
      setNonWorkingDays(daysResponse.dias);
      
      // Converter estatísticas detalhadas para o formato esperado
      const convertedStats: DiaNaoFuncionamentoStatistics = {
        totalDias: statsResponse.geral.total,
        diasAtivos: statsResponse.geral.ativos,
        diasRecorrentes: statsResponse.geral.recorrentes,
        diasAutomaticos: (statsResponse.geral as DiaNaoFuncionamentoEstatisticasDetalhadas['geral'] & { automaticos?: number }).automaticos || 0,
        porTipo: statsResponse.porTipo
      };
      setStatistics(convertedStats);
    } catch (error: unknown) {
      console.error('Erro ao carregar dados:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao carregar dados';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NOVA FUNCIONALIDADE: Carregar todos os dias (incluindo automáticos)
  const loadAllDays = async () => {
    try {
      setLoading(true);
      console.log('🔍 Carregando TODOS os dias (incluindo automáticos)...');
      
      const [daysResponse, statsResponse] = await Promise.all([
        DiaNaoFuncionamentoService.getDiasNaoFuncionamento(),
        DiaNaoFuncionamentoService.getEstatisticas()
      ]);

      console.log('📊 Dados recebidos:', {
        totalDias: daysResponse.dias.length,
        diasAutomaticos: daysResponse.dias.filter(d => d.automatico).length,
        diasManuais: daysResponse.dias.filter(d => !d.automatico).length
      });

      setNonWorkingDays(daysResponse.dias);
      // Converter estatísticas detalhadas para o formato esperado
      const convertedStats: DiaNaoFuncionamentoStatistics = {
        totalDias: statsResponse.geral.total,
        diasAtivos: statsResponse.geral.ativos,
        diasRecorrentes: statsResponse.geral.recorrentes,
        diasAutomaticos: (statsResponse.geral as DiaNaoFuncionamentoEstatisticasDetalhadas['geral'] & { automaticos?: number }).automaticos || 0,
        porTipo: statsResponse.porTipo
      };
      setStatistics(convertedStats);
      console.log('✅ Dados carregados com sucesso');
    } catch (error: unknown) {
      console.error('❌ Erro ao carregar dados:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao carregar dados';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ✅ CORREÇÃO: Carregar apenas dias manuais inicialmente (sem feriados automáticos)
    loadManualDays();
  }, []);

  const filteredDays = nonWorkingDays.filter(day =>
    day.motivo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    day.tipo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    day.data.includes(searchTerm)
  );

  // Separar feriados automáticos dos manuais
  const automaticHolidays = filteredDays.filter(day => day.automatico);
  const manualDays = filteredDays.filter(day => !day.automatico);

  const handleOpenDialog = (day?: DiaNaoFuncionamento) => {
    if (day) {
      setEditingDay(day);
      setFormData({
        data: day.data,
        motivo: day.motivo,
        descricao: day.descricao || "",
        ativo: day.ativo,
        recorrente: day.recorrente,
        tipo: day.tipo
      });
    } else {
      setEditingDay(null);
      setFormData({
        data: "",
        motivo: "",
        descricao: "",
        ativo: true,
        recorrente: false,
        tipo: TipoDiaNaoFuncionamento.FERIADO
      });
    }
    setIsDialogOpen(true);
  };

  const handleSaveDay = async () => {
    try {
      if (!formData.data || !formData.motivo) {
        toast.error('Data e motivo são obrigatórios');
        return;
      }

      if (editingDay) {
        await DiaNaoFuncionamentoService.updateDiaNaoFuncionamento(editingDay.id, formData);
        toast.success('Dia não funcionamento atualizado com sucesso!');
      } else {
        await DiaNaoFuncionamentoService.createDiaNaoFuncionamento(formData as CreateDiaNaoFuncionamentoRequest);
        toast.success('Dia não funcionamento criado com sucesso!');
      }

      setIsDialogOpen(false);
      setEditingDay(null);
      await loadAllDays();
    } catch (error: unknown) {
      console.error('Erro ao salvar dia não funcionamento:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao salvar dia não funcionamento';
      toast.error(message);
    }
  };

  const handleDeleteDay = (day: DiaNaoFuncionamento) => {
    setDayToDelete(day);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDay = async () => {
    if (!dayToDelete) return;

    try {
      await DiaNaoFuncionamentoService.deleteDiaNaoFuncionamento(dayToDelete.id);
      toast.success('Dia não funcionamento excluído com sucesso!');
              await loadAllDays();
        setDeleteDialogOpen(false);
      setDayToDelete(null);
    } catch (error: unknown) {
      console.error('Erro ao excluir dia não funcionamento:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao excluir dia não funcionamento';
      toast.error(message);
    }
  };

  const toggleDayStatus = async (dayId: string) => {
    try {
      await DiaNaoFuncionamentoService.toggleStatus(dayId);
      toast.success('Status atualizado com sucesso!');
      await loadAllDays();
    } catch (error: unknown) {
      console.error('Erro ao atualizar status:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao atualizar status';
      toast.error(message);
    }
  };

  const handleSyncHolidays = async (ano?: number) => {
    try {
      setSyncLoading(true);
      
      const currentYear = ano || new Date().getFullYear();
      console.log('🔄 Iniciando sincronização para o ano:', currentYear);
      
      const result = await DiaNaoFuncionamentoService.sincronizarFeriados(currentYear);
      console.log('📥 Resultado da sincronização:', result);
      
      if (result.success) {
        const { importados, atualizados } = result.dados;
        const total = importados + atualizados;
        
        console.log(`✅ Sincronização bem-sucedida: ${importados} importados, ${atualizados} atualizados`);
        
        if (importados > 0 || atualizados > 0) {
          toast.success(`✅ Sincronização concluída! ${importados} novos feriados, ${atualizados} atualizados de ${total} processados.`);
        } else {
          toast.info(`✅ Sincronização concluída! Todos os feriados já estavam atualizados.`);
        }
        
        console.log('🔄 Recarregando todos os dias...');
        await loadAllDays();
        console.log('✅ Dados recarregados');
        setPreviewDialogOpen(false);
      } else {
        console.error('❌ Sincronização falhou:', result);
        throw new Error(result.message || 'Erro na sincronização');
      }
    } catch (error: unknown) {
      console.error('❌ Erro na sincronização:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido na sincronização';
      toast.error(`❌ Erro na sincronização: ${message}`);
    } finally {
      setSyncLoading(false);
    }
  };

  const handlePreviewHolidays = async (year: number) => {
    try {
      setPreviewLoading(true);
      
      
      const response = await DiaNaoFuncionamentoService.importarFeriadosAno(year);
      
      if (response.success) {
        setPreviewHolidays(response.dados.feriados);
      } else {
        setPreviewHolidays([]);
        toast.error(response.message || 'Não foi possível carregar o preview dos feriados.');
      }

    } catch (error: unknown) {
      console.error('Erro ao carregar preview dos feriados:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao carregar preview dos feriados';
      toast.error(message);
      setPreviewHolidays([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const getTypeColor = (tipo: string) => {
    switch (tipo) {
      case "FERIADO": return "bg-green-100 text-green-800";
      case "MANUTENCAO": return "bg-orange-100 text-orange-800";
      case "EVENTO": return "bg-blue-100 text-blue-800";
      case "FECHAMENTO": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeLabel = (tipo: string) => {
    switch (tipo) {
      case "FERIADO": return "Feriado";
      case "MANUTENCAO": return "Manutenção";
      case "EVENTO": return "Evento";
      case "FECHAMENTO": return "Fechamento";  
      default: return tipo;
    }
  };

  // Configuração das colunas da tabela responsiva
  const { createColumn } = useTableColumns<DiaNaoFuncionamento>();
  
  const columns: ColumnDef<DiaNaoFuncionamento>[] = [
    createColumn('data', 'Data', (day) => (
      <div className="flex items-center gap-1">
        <span className="font-medium">{formatCompact(day.data)}</span>
        {day.automatico && (
          <Clock className="w-3 h-3 text-blue-500" />
        )}
      </div>
    )),
    createColumn('motivo', 'Motivo', (day) => day.motivo),
    createColumn('tipo', 'Tipo', (day) => (
      <Badge className={getTypeColor(day.tipo)}>
        {getTypeLabel(day.tipo)}
      </Badge>
    )),
    createColumn('origem', 'Origem', (day) => (
      <Badge 
        variant={day.automatico ? "default" : "outline"}
        className={day.automatico ? "bg-blue-100 text-blue-800" : ""}
      >
        {day.automatico ? "Automático" : "Manual"}
      </Badge>
    )),
    createColumn('status', 'Status', (day) => (
      <Badge variant={day.ativo ? "default" : "secondary"}>
        {day.ativo ? "Ativo" : "Inativo"}
      </Badge>
    )),
    createColumn('recorrente', 'Recorrente', (day) => (
      day.recorrente ? (
        <Badge variant="outline">Sim</Badge>
      ) : (
        <span className="text-gray-500">Não</span>
      )
    )),
    createColumn('actions', 'Ações', (day) => (
      <div className="flex gap-2 justify-end">
        {!day.automatico && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOpenDialog(day)}
            title="Editar"
          >
            <Edit className="w-4 h-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => toggleDayStatus(day.id)}
          title="Alternar Status"
        >
          <CheckCircle className="w-4 h-4" />
        </Button>
        {!day.automatico && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteDay(day)}
            className="text-red-600 hover:text-red-800"
            title="Excluir"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    ))
  ];

  // Card mobile personalizado para dias não funcionamento
  const renderDayMobileCard = (day: DiaNaoFuncionamento, index: number) => (
    <Card key={index} className={`p-4 ${day.automatico ? 'bg-blue-50' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-lg">{formatCompact(day.data)}</h3>
          {day.automatico && (
            <Clock className="w-4 h-4 text-blue-500" />
          )}
        </div>
        <Badge variant={day.ativo ? "default" : "secondary"}>
          {day.ativo ? "Ativo" : "Inativo"}
        </Badge>
      </div>
      
      <div className="space-y-2 text-sm mb-4">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Motivo:</span>
          <span className="font-medium">{day.motivo}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Tipo:</span>
          <Badge className={getTypeColor(day.tipo)}>
            {getTypeLabel(day.tipo)}
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Origem:</span>
          <Badge 
            variant={day.automatico ? "default" : "outline"}
            className={day.automatico ? "bg-blue-100 text-blue-800" : ""}
          >
            {day.automatico ? "Automático" : "Manual"}
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Recorrente:</span>
          <span>{day.recorrente ? "Sim" : "Não"}</span>
        </div>
        {day.descricao && (
          <div className="pt-2 border-t">
            <span className="text-gray-500">Descrição:</span>
            <p className="text-sm mt-1">{day.descricao}</p>
          </div>
        )}
      </div>
      
      <div className="flex gap-2">
        <Button 
          size="sm" 
          variant="outline"
          onClick={() => toggleDayStatus(day.id)}
          className="flex-1"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          {day.ativo ? 'Desativar' : 'Ativar'}
        </Button>
        {!day.automatico && (
          <>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleOpenDialog(day)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleDeleteDay(day)}
              className="text-red-600 hover:text-red-800"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </Card>
  );

  if (loading) {
    return (
      <ResponsiveAdminLayout 
        title="Dias Não Funcionamento" 
        subtitle="Gerencie feriados e dias de fechamento"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Carregando dados...</p>
          </div>
        </div>
      </ResponsiveAdminLayout>
    );
  }

  return (
    <ResponsiveAdminLayout 
      title="Dias Não Funcionamento" 
      subtitle="Gerencie feriados e dias de fechamento"
    >
      {/* Search and Action Buttons */}
      <div className={`mb-6 flex ${isMobile ? 'flex-col gap-4' : 'items-center justify-between gap-4'}`}>
        <Input
          placeholder="Buscar por motivo, tipo ou data..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={isMobile ? 'w-full' : 'max-w-md'}
        />
        
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center gap-2'}`}>
          {/* Preview Button - Visualizar feriados antes de sincronizar */}
          <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                onClick={() => handlePreviewHolidays(new Date().getFullYear())}
                title="Visualizar feriados nacionais antes de importar"
                size={isMobile ? "sm" : "default"}
                className={isMobile ? 'w-full' : ''}
              >
                <Eye className="w-4 h-4 mr-2" />
                {isMobile ? 'Visualizar' : 'Visualizar Feriados'}
              </Button>
            </DialogTrigger>
            <DialogContent className={`${isMobile ? 'max-w-[95vw] h-[90vh] overflow-y-auto' : 'max-w-2xl'}`}>
              <DialogHeader>
                <DialogTitle>
                  🇧🇷 Feriados Nacionais Brasileiros - {previewYear}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center gap-2'}`}>
                  <Label htmlFor="preview-year">Ano:</Label>
                  <TouchInput
                    id="preview-year"
                    type="number"
                    value={previewYear}
                    onChange={(e) => setPreviewYear(parseInt(e.target.value))}
                    className={isMobile ? 'w-full' : 'w-24'}
                    min="2020"
                    max="2030"
                  />
                  <Button 
                    size="sm" 
                    onClick={() => handlePreviewHolidays(previewYear)}
                    disabled={previewYear < 2020 || previewYear > 2030 || previewLoading}
                    className={isMobile ? 'w-full' : ''}
                  >
                    {previewLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    {previewLoading ? 'Carregando...' : 'Atualizar'}
                  </Button>
                </div>
                
                {previewHolidays.length > 0 ? (
                  <div className={`${isMobile ? 'max-h-60' : 'max-h-96'} overflow-y-auto`}>
                    {isMobile ? (
                      <div className="space-y-2">
                        {previewHolidays.map((holiday, index) => (
                          <Card key={index} className="p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{holiday.nome}</p>
                                <p className="text-sm text-gray-500">{formatCompact(holiday.data)}</p>
                              </div>
                              <Badge className="bg-green-100 text-green-800">
                                {holiday.tipo}
                              </Badge>
                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Data</TableHead>
                            <TableHead>Nome do Feriado</TableHead>
                            <TableHead>Tipo</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {previewHolidays.map((holiday, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">
                                {formatCompact(holiday.data)}
                              </TableCell>
                              <TableCell>{holiday.nome}</TableCell>
                              <TableCell>
                                <Badge className="bg-green-100 text-green-800">
                                  {holiday.tipo}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    <p className="text-sm text-gray-600 mt-2">
                      📊 Total: {previewHolidays.length} feriados encontrados para {previewYear}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum feriado encontrado para {previewYear}</p>
                  </div>
                )}
                
                <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-end gap-2'}`}>
                  <Button 
                    variant="outline" 
                    onClick={() => setPreviewDialogOpen(false)}
                    className={isMobile ? 'w-full' : ''}
                  >
                    Fechar
                  </Button>
                  {previewHolidays.length > 0 && (
                    <Button 
                      onClick={() => {
                        setPreviewDialogOpen(false);
                        handleSyncHolidays(previewYear);
                      }}
                      className={isMobile ? 'w-full' : ''}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Sincronizar Agora
                    </Button>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Sync Button - Importar feriados automaticamente */}
          <Button 
            variant="outline" 
            onClick={() => handleSyncHolidays()}
            disabled={syncLoading}
            title="Importar feriados nacionais brasileiros automaticamente"
            size={isMobile ? "sm" : "default"}
            className={isMobile ? 'w-full' : ''}
          >
            {syncLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {syncLoading ? 'Sincronizando...' : isMobile ? 'Sincronizar' : 'Sincronizar Feriados'}
          </Button>

          {/* Add Manual Day Button */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()}
                size={isMobile ? "sm" : "default"}
                className={`bg-blue-600 hover:bg-blue-700 text-white ${isMobile ? 'w-full' : ''}`}
              >
                <Plus className="w-4 h-4 mr-2" />
                {isMobile ? 'Novo' : 'Novo Dia Manual'}
              </Button>
            </DialogTrigger>
            <DialogContent className={`${isMobile ? 'max-w-[95vw] h-[90vh] overflow-y-auto' : 'max-w-md'}`}>
              <DialogHeader>
                <DialogTitle>
                  {editingDay ? 'Editar Dia Não Funcionamento' : 'Novo Dia Não Funcionamento'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="data">Data *</Label>
                  <TouchInput
                    id="data"
                    type="date"
                    value={editingDay ? formatForInput(formData.data) : formData.data}
                    onChange={(e) => setFormData({...formData, data: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="motivo">Motivo *</Label>
                  <TouchInput
                    id="motivo"
                    value={formData.motivo}
                    onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                    placeholder="Ex: Natal, Manutenção..."
                  />
                </div>

                <div>
                  <Label htmlFor="tipo">Tipo *</Label>
                  <select
                    id="tipo"
                    value={formData.tipo}
                    onChange={(e) => setFormData({...formData, tipo: e.target.value as TipoDiaNaoFuncionamento})}
                    className={`w-full p-2 border border-gray-300 rounded-lg ${isMobile ? 'min-h-[44px]' : ''}`}
                  >
                    <option value="FERIADO">Feriado</option>
                    <option value="MANUTENCAO">Manutenção</option>
                    <option value="EVENTO">Evento</option>
                    <option value="FECHAMENTO">Fechamento</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    value={formData.descricao}
                    onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                    placeholder="Descrição adicional..."
                    rows={3}
                    className={isMobile ? 'min-h-[100px]' : ''}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="recorrente"
                    checked={formData.recorrente}
                    onCheckedChange={(checked) => setFormData({...formData, recorrente: checked})}
                  />
                  <Label htmlFor="recorrente">Recorrente (anual)</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="ativo"
                    checked={formData.ativo}
                    onCheckedChange={(checked) => setFormData({...formData, ativo: checked})}
                  />
                  <Label htmlFor="ativo">Ativo</Label>
                </div>

                <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-end space-x-2'} pt-4`}>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className={isMobile ? 'w-full' : ''}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleSaveDay}
                    className={isMobile ? 'w-full' : ''}
                  >
                    {editingDay ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Days Table */}
      <Card>
        <CardHeader className={isMobile ? 'p-4' : ''}>
          <CardTitle className={isMobile ? 'text-base' : ''}>
            Lista de Dias Não Funcionamento ({filteredDays.length})
          </CardTitle>
        </CardHeader>
        <CardContent className={isMobile ? 'p-4 pt-0' : ''}>
          <ResponsiveTable
            data={filteredDays}
            columns={columns}
            renderMobileCard={renderDayMobileCard}
            loading={false}
            emptyMessage={searchTerm ? 'Nenhum resultado encontrado' : 'Nenhum dia não funcionamento cadastrado'}
          />
        </CardContent>
      </Card>

      {/* Modal de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o dia não funcionamento <strong>"{dayToDelete?.motivo}"</strong> de <strong>{dayToDelete ? formatCompact(dayToDelete.data) : ''}</strong>?
              <br /><br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteDay}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ResponsiveAdminLayout>
  );
};

export default AdminNonWorkingDays; 